import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { usePostHog } from '@posthog/react';
import { AppointmentTypes, LocationTypes } from 'common';
import { BookingWizardFlowTypes } from 'common/dist/infrastructure/modules/appointment/interfaces/AppointmentTypes';
import { UserSummary } from 'common/dist/infrastructure/modules/auth/interfaces/UserTypes';
import { useCallback, useEffect } from 'react';

import {
  BookAppointmentValues,
  IConfirmReservationAdapter,
  ServicePricingError,
} from 'application/modules/bookingWizard/useCases/hooks/useCaseConfirmReservation';
import useCaseGetSimulatorLocations from 'application/modules/bookingWizard/useCases/hooks/useCaseGetSimulatorLocations';
import useCaseExternalNavigation from 'application/modules/bookingWizard/useCases/hooks/useCaseNavigateTo';
import mboAPI from 'infrastructure/redux/adapters/mboAPI/mboApi';
import { selectAccessToken } from 'infrastructure/redux/slices/auth.selector';
import { clearAuthData } from 'infrastructure/redux/slices/auth.slice';
import {
  selectAppointments,
  selectEndTime,
  selectExperienceType,
  selectInstructor,
  selectLocation,
  selectPartySize,
  selectResourceId,
  selectStartTime,
} from 'infrastructure/redux/slices/bookingWizard.selector';
import {
  clearBookingData,
  setAddons,
  setAppointments,
  setConfirmedBooking,
  setLocationDetails,
  setPartySize,
} from 'infrastructure/redux/slices/bookingWizard.slice';
import { clearPaymentData, setTotalAmountToPay } from 'infrastructure/redux/slices/payment.slice';
import { clearUserData, setUserSummary } from 'infrastructure/redux/slices/user.slice';
import { getReduxStoreVersion } from 'infrastructure/redux/store';
import { useAppDispatch, useAppSelector } from 'infrastructure/redux/store/hooks';
import {
  mapConfirmReservationError,
} from 'infrastructure/redux/adapters/bookingWizard/confirmReservationErrorMapper';

function getPricingError(
  error: FetchBaseQueryError | SerializedError | undefined,
): ServicePricingError | undefined {
  if (!error) {
    return undefined;
  }
  return ServicePricingError.Unknown;
}

const useConfirmReservation: IConfirmReservationAdapter = () => {
  const posthog = usePostHog();
  const dispatch = useAppDispatch();
  const locationId = useAppSelector(selectLocation);
  const startTime = useAppSelector(selectStartTime);
  const endTime = useAppSelector(selectEndTime);
  const staffId = useAppSelector(selectInstructor);
  const resourceId = useAppSelector(selectResourceId);
  const appointments = useAppSelector(selectAppointments);
  const flowType = useAppSelector(selectExperienceType);
  const partySize = useAppSelector(selectPartySize);
  const isLessonFlow =
    flowType === BookingWizardFlowTypes.Lesson ||
    flowType === BookingWizardFlowTypes.FirstLesson ||
    flowType === BookingWizardFlowTypes.ClubFitting ||
    flowType === BookingWizardFlowTypes.ClubFittingbyCoach ||
    flowType === BookingWizardFlowTypes.LessonByCoach ||
    flowType === BookingWizardFlowTypes.LessonByDate;
  const { result: locations } = useCaseGetSimulatorLocations();

  const [bookAppointmentTrigger, result] = mboAPI.useBookAppointmentMutation();
  const [getUser] = mboAPI.useLazyGetUserDataQuery();
  const { navigateToSignInPage, navigateToMyBookingsPage } = useCaseExternalNavigation();
  const accessToken = useAppSelector(selectAccessToken);
  const [getPrices, { data: pricingResult, isLoading, error }] =
    mboAPI.useGetAppointmentPricingMutation();

  const handleLogout = () => {
    posthog?.reset();
    dispatch(clearUserData());
    dispatch(clearAuthData());
    dispatch(clearPaymentData());
    dispatch(clearBookingData());

    localStorage.removeItem(`persist:${getReduxStoreVersion()}`);
    navigateToSignInPage();
  };

  const handleSaveUserDataToStore = (userSummary: UserSummary | undefined) => {
    dispatch(setUserSummary(userSummary));
  };
  const getLocationDetails = useCallback(
    (locationId?: string) => locations?.find((location) => location.id === locationId),
    [locations],
  );
  const saveLocationDetailsToStore = (selectedSimLocation: LocationTypes.SimulatorLocation) => {
    dispatch(setLocationDetails(selectedSimLocation));
  };

  const handleMyBookingsRedirect = () => {
    navigateToMyBookingsPage();
  };

  useEffect(() => {
    const details = getLocationDetails(locationId);
    if (details) {
      saveLocationDetailsToStore(details);
    }
  }, [getLocationDetails]);

  useEffect(() => {
    if (!partySize || partySize === '') {
      // Default to 1 if party size is not set or invalid
      console.warn('Invalid party size detected, defaulting to 1');
      dispatch(setPartySize(1));
    }
  }, [partySize, dispatch]);

  const handleGetUser = () => {
    if (locationId) {
      return getUser(locationId).unwrap();
    }
  };

  const handleGetPricing = (userSummary?: UserSummary) => {
    if (locationId && startTime && endTime) {
      if (isLessonFlow) {
        const staffIdArray: number[] = appointments?.map((app) => Number(app.staffId)) || [];
        getPrices({
          sessionTypeId: appointments?.[0]?.sessionTypeId,
          email: userSummary?.email,
          startDateTime: startTime,
          endDateTime: endTime,
          locationId: locationId,
          staffIds: staffIdArray,
          partySize,
        });
      } else if (staffId) {
        let staffIdArray;
        if (typeof staffId === 'number') {
          staffIdArray = [staffId];
        } else {
          staffIdArray = staffId.split(',').map(Number);
        }

        getPrices({
          email: userSummary?.email,
          startDateTime: startTime,
          endDateTime: endTime,
          locationId: locationId,
          staffIds: staffIdArray,
          partySize,
        });
      }
    }
  };

  const handleSaveConfirmedBookingToStore = (
    result: AppointmentTypes.BookedAppointmentResponse,
  ) => {
    dispatch(setConfirmedBooking(result));
  };

  const handleSaveAppointmentsToStore = (
    appointments: AppointmentTypes.Appointment[] | undefined,
    total: number | undefined,
    addOn?: AppointmentTypes.AddOnType,
  ) => {
    if (resourceId !== undefined) {
      dispatch(
        setAppointments({ appointments: appointments?.map((value) => ({ ...value, resourceId })) }),
      );
    } else {
      dispatch(setAppointments({ appointments: appointments }));
    }

    dispatch(setAddons(addOn));
    dispatch(setTotalAmountToPay({ total }));
  };

  const bookAppointment = async (values: BookAppointmentValues): Promise<void> => {
    await bookAppointmentTrigger(values).unwrap();
  };

  return {
    error: mapConfirmReservationError(result.error),
    result: result.data,
    inProgress: result.isLoading,
    bookAppointment,
    handleLogout,
    getUser: handleGetUser,
    handleSaveUserDataToStore,
    accessToken,
    getPricing: handleGetPricing,
    pricingResult,
    isPricingLoading: isLoading,
    pricingError: getPricingError(error),
    handleSaveAppointmentsToStore,
    handleSaveConfirmedBookingToStore,
    handleMyBookingsRedirect,
  };
};

export default useConfirmReservation;
